import React, { useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import {
  PRICE_INPUT_REGEX,
  stripCommas,
} from '@/domain/pricing/utils/pricing-utils';
import { TextInput, View } from 'react-native';
import { useReportCellFocus } from '@experiences/components/table/blocs/hooks/use-report-cell-focus';

interface EditablePriceCellProps {
  currencyCode: string;
  localId: string;
  onUpdatePrice: (localId: string, price: string) => void;
  price: string;
  rowIndex: number;
}

/**
 * Inline-editable price cell for the quote line-items table. Shows the
 * formatted currency while idle and the raw numeric string while focused
 * so the rep can edit without fighting the `$` / thousand-separator
 * formatting. Commits on blur; resets to the last-known good value when
 * the input doesn't parse as a non-negative number.
 */
export const EditablePriceCell: React.FC<EditablePriceCellProps> = ({
  currencyCode,
  localId,
  onUpdatePrice,
  price,
  rowIndex,
}) => {
  const [styles, theme] = useStyles();
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState(price);
  const reportCellFocus = useReportCellFocus(rowIndex);

  const handleFocus = () => {
    reportCellFocus();
    setValue(price);
    setIsFocused(true);
  };

  const handleChangeText = (text: string) => {
    if (text && !PRICE_INPUT_REGEX.test(text)) {
      return;
    }
    setValue(stripCommas(text));
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseFloat(value);
    // Reject zero — a $0 line item has no place in a quote; rep should
    // remove it instead. Matches the quantity cell's positive-only rule.
    if (isNaN(parsed) || parsed <= 0) {
      setValue(price);
      return;
    }
    // Skip commit on a no-op blur so just tabbing through doesn't mark
    // the quote dirty.
    if (value === price) {
      return;
    }
    onUpdatePrice(localId, value);
  };

  return (
    <View style={styles.container}>
      <TextInput
        keyboardType="decimal-pad"
        onBlur={handleBlur}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        placeholder="0.00"
        placeholderTextColor={theme.sk.color.text.tertiary}
        selectTextOnFocus
        style={[styles.input, isFocused && styles.inputFocused]}
        value={
          isFocused
            ? value
            : formatCurrency(parseFloat(price) * MICRO_CONVERSION, currencyCode)
        }
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'flex-start',
    flex: 1,
    justifyContent: 'center',
  },
  input: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    fontFamily: theme.sk.typography.paragraph.md.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
    textAlign: 'left',
    width: '100%',
  },
  inputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
}));
